/* ==========================================================================
   Maštarije Nova — mst-group-order.js

     <mst-group-order data-section data-cart-add-url data-base-variant
                      data-base-price data-per-unit data-addon-per-units>
       Order builder for set products sold per child (B2B: kindergartens,
       workshops, schools). One unit stepper ("for how many children"),
       N optional add-on products each with their own quantity, a live
       line-by-line summary, and a single add-to-cart that posts EVERY
       selected line in one request:

         POST /cart/add.js  {items: [{id, quantity}, ...], sections: ...}

       The multi-item JSON payload is why this cannot reuse <mst-product-form>
       (that one posts a single FormData line).

       Add-on quantities follow the unit count automatically —
       ceil(units / addon-per-units), i.e. one add-on per started group of
       10 children — until the shopper edits a quantity by hand. From then
       on that add-on keeps its own value and offers a "back to suggested"
       reset. Editor-safe: state lives on the element, so a section
       re-render rebuilds it from scratch.

       Reuses <mst-quantity> (mst-core.js) for every stepper, so clamping,
       min/max and button disabling behave exactly like the rest of the PDP.
   ========================================================================== */

(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.MST && typeof window.MST.formatMoney === 'function') {
      return window.MST.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function patchSectionHTML(sectionHTML, sourceSelector, target) {
    if (!sectionHTML || !target) return;
    var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    var source = sourceSelector ? doc.querySelector(sourceSelector) : doc.body;
    if (source) target.innerHTML = source.innerHTML;
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /* Croatian (and other Slavic) plural selection. The three word forms come
     from the locale files via data attributes so this stays translatable:
       one   1, 21, 31 …  (but not 11)
       few   2–4, 22–24 … (but not 12–14)
       other everything else
     Locales without a "few" form simply repeat the "other" string. */
  function pluralize(count, one, few, other) {
    var abs = Math.abs(count);
    var mod10 = abs % 10;
    var mod100 = abs % 100;

    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return other;
  }

  /* Client-side placeholders use [square] brackets, NOT {{ curly }} ones:
     Shopify interpolates {{ … }} inside translation strings when the `t`
     filter runs, so a curly placeholder meant for JS would be consumed
     server-side before it ever reaches us. */
  function fillTemplate(template, values) {
    if (!template) return '';
    return template.replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key)
        ? String(values[key])
        : match;
    });
  }

  if (customElements.get('mst-group-order')) return;

  customElements.define(
    'mst-group-order',
    class MstGroupOrder extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section || '';
        this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
        if (this.cartAddUrl.indexOf('.js') === -1) this.cartAddUrl += '.js';
        this.cartUrl = this.cartAddUrl.replace(/\/add\.js$/, '.js');

        this.baseVariantId = this.dataset.baseVariant || '';
        this.basePrice = toInt(this.dataset.basePrice, 0);
        this.perUnit = Math.max(1, toInt(this.dataset.perUnit, 1));
        this.addonPerUnits = Math.max(
          1,
          toInt(this.dataset.addonPerUnits, 10)
        );

        this.form = this.querySelector('form');
        this.unitsInput = this.querySelector('[data-units-input]');
        this.unitsSummary = this.querySelector('[data-units-summary]');
        this.submitBtn = this.querySelector('[data-group-submit]');
        this.errorEl = this.querySelector('[data-group-error]');
        this.totalEl = this.querySelector('[data-group-total]');
        this.liveNote = this.querySelector('[data-live-note]');
        this.liveNoteText = this.querySelector('[data-live-note-text]');

        if (!this.form || !this.unitsInput) return;

        // JS owns validation from here on; the unstyled native bubbles are
        // only there for the no-JS path.
        this.form.noValidate = true;

        this.baseLine = this.querySelector('[data-line="base"]');

        this.addons = Array.prototype.map.call(
          this.querySelectorAll('[data-addon]'),
          function (row) {
            return {
              row: row,
              key: row.dataset.addon,
              variantId: row.dataset.variantId || '',
              price: toInt(row.dataset.price, 0),
              available: row.dataset.available !== 'false',
              toggle: row.querySelector('[data-addon-toggle]'),
              qtyInput: row.querySelector('[data-addon-qty]'),
              resetBtn: row.querySelector('[data-addon-reset]'),
              line: row
                .closest('mst-group-order')
                .querySelector('[data-line="' + row.dataset.addon + '"]'),
              // false until the shopper edits this add-on's quantity by hand
              overridden: false
            };
          }
        );

        this.onUnitsChangeBound = this.onUnitsChange.bind(this);
        this.unitsInput.addEventListener('change', this.onUnitsChangeBound);
        this.unitsInput.addEventListener('input', this.onUnitsChangeBound);

        this.bindAddons();

        this.onSubmitBound = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmitBound);

        this.render();
      }

      disconnectedCallback() {
        if (this.unitsInput && this.onUnitsChangeBound) {
          this.unitsInput.removeEventListener('change', this.onUnitsChangeBound);
          this.unitsInput.removeEventListener('input', this.onUnitsChangeBound);
        }
        if (this.form && this.onSubmitBound) {
          this.form.removeEventListener('submit', this.onSubmitBound);
        }
        (this.addons || []).forEach(function (addon) {
          if (addon.toggle && addon.onToggle) {
            addon.toggle.removeEventListener('change', addon.onToggle);
          }
          if (addon.qtyInput && addon.onQty) {
            addon.qtyInput.removeEventListener('change', addon.onQty);
            addon.qtyInput.removeEventListener('input', addon.onQty);
          }
          if (addon.resetBtn && addon.onReset) {
            addon.resetBtn.removeEventListener('click', addon.onReset);
          }
        });
      }

      bindAddons() {
        var self = this;

        this.addons.forEach(function (addon) {
          if (addon.toggle) {
            addon.onToggle = function () {
              // Turning an add-on on re-arms the suggestion: the shopper has
              // not expressed an opinion about ITS quantity yet.
              if (addon.toggle.checked && !addon.overridden) {
                self.applySuggestion(addon);
              }
              self.render();
            };
            addon.toggle.addEventListener('change', addon.onToggle);
          }

          if (addon.qtyInput) {
            addon.onQty = function (event) {
              // Programmatic writes set a flag so applySuggestion() does not
              // mark itself as a manual override.
              if (!self.suppressOverride) addon.overridden = true;
              if (event && event.type === 'change') self.render();
              else self.renderTotals();
            };
            addon.qtyInput.addEventListener('change', addon.onQty);
            addon.qtyInput.addEventListener('input', addon.onQty);
          }

          if (addon.resetBtn) {
            addon.onReset = function (event) {
              event.preventDefault();
              addon.overridden = false;
              self.applySuggestion(addon);
              self.render();
            };
            addon.resetBtn.addEventListener('click', addon.onReset);
          }
        });
      }

      units() {
        var min = toInt(this.unitsInput.min, 1);
        var value = toInt(this.unitsInput.value, min);
        return Math.max(min, value);
      }

      /* One add-on per started group of `addonPerUnits` children:
         1–10 → 1, 11–20 → 2, 21–30 → 3 … */
      suggestedAddonQty() {
        return Math.max(1, Math.ceil(this.units() / this.addonPerUnits));
      }

      applySuggestion(addon) {
        if (!addon.qtyInput) return;
        var suggested = this.suggestedAddonQty();
        if (toInt(addon.qtyInput.value, 1) === suggested) return;

        this.suppressOverride = true;
        addon.qtyInput.value = suggested;
        // Let <mst-quantity> re-clamp and refresh its +/- disabled states.
        addon.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.suppressOverride = false;
      }

      onUnitsChange() {
        var self = this;
        this.addons.forEach(function (addon) {
          if (addon.toggle && addon.toggle.checked && !addon.overridden) {
            self.applySuggestion(addon);
          }
        });
        this.render();
      }

      addonQty(addon) {
        if (!addon.qtyInput) return 1;
        return Math.max(1, toInt(addon.qtyInput.value, 1));
      }

      isActive(addon) {
        return !!(addon.toggle && addon.toggle.checked && addon.available);
      }

      render() {
        this.renderUnits();
        this.renderAddons();
        this.renderTotals();
        this.renderLiveNote();
      }

      renderUnits() {
        if (!this.unitsSummary) return;
        var units = this.units();
        var pieces = units * this.perUnit;

        var setWord = pluralize(
          units,
          this.dataset.wordSetOne,
          this.dataset.wordSetFew,
          this.dataset.wordSetOther
        );
        var pieceWord = pluralize(
          pieces,
          this.dataset.wordPieceOne,
          this.dataset.wordPieceFew,
          this.dataset.wordPieceOther
        );

        this.unitsSummary.textContent = fillTemplate(
          this.dataset.unitsSummary,
          {
            units: units,
            setWord: setWord,
            pieces: pieces,
            pieceWord: pieceWord
          }
        );
      }

      renderAddons() {
        var self = this;
        var suggested = this.suggestedAddonQty();

        this.addons.forEach(function (addon) {
          var on = self.isActive(addon);
          addon.row.classList.toggle('is-on', on);

          if (addon.resetBtn) {
            // The reset only makes sense once a manual value has drifted
            // away from what we would suggest.
            addon.resetBtn.hidden =
              !on || !addon.overridden || self.addonQty(addon) === suggested;
            addon.resetBtn.textContent = fillTemplate(
              self.dataset.resetLabel,
              { qty: suggested }
            );
          }
        });
      }

      renderTotals() {
        var self = this;
        var units = this.units();
        var total = units * this.basePrice;

        if (this.baseLine) {
          this.renderLine(this.baseLine, units, this.basePrice);
        }

        this.addons.forEach(function (addon) {
          var on = self.isActive(addon);
          var qty = self.addonQty(addon);
          if (on) total += qty * addon.price;
          if (addon.line) {
            addon.line.hidden = !on;
            if (on) self.renderLine(addon.line, qty, addon.price);
          }
        });

        if (this.totalEl) this.totalEl.textContent = formatMoney(total);
        this.syncStickyPrice(total);
      }

      renderLine(line, qty, unitPrice) {
        var qtyEl = line.querySelector('[data-line-qty]');
        var amountEl = line.querySelector('[data-line-amount]');
        if (qtyEl) {
          qtyEl.textContent = fillTemplate(line.dataset.qtyTemplate, {
            qty: qty,
            price: formatMoney(unitPrice)
          });
        }
        if (amountEl) amountEl.textContent = formatMoney(qty * unitPrice);
      }

      /* The mobile sticky bar normally mirrors #price-<section>. On this
         template the meaningful number is the running total, so we write it
         in directly. #price-<section> never mutates here (single variant, no
         picker), so <mst-sticky-atc>'s MutationObserver never fights us —
         but we still re-assert on the next frame to win the initial race
         with its own connectedCallback. */
      syncStickyPrice(total) {
        var bar = document.querySelector(
          'mst-sticky-atc[data-section="' + this.sectionId + '"]'
        );
        var out = bar && bar.querySelector('[data-sticky-price]');
        if (!out) return;

        var text = formatMoney(total);
        out.textContent = text;

        if (!this.stickyReasserted) {
          this.stickyReasserted = true;
          requestAnimationFrame(function () {
            out.textContent = text;
          });
        }
      }

      renderLiveNote() {
        if (!this.liveNote) return;
        var units = this.units();

        if (units <= this.addonPerUnits) {
          this.liveNote.hidden = true;
          return;
        }

        var suggested = this.suggestedAddonQty();
        var setWord = pluralize(
          suggested,
          this.dataset.wordSetOne,
          this.dataset.wordSetFew,
          this.dataset.wordSetOther
        );
        // "41 dijete", "22 djeteta", "25 djece" — the count word declines
        // with the number too, not just the noun it modifies.
        var childWord = pluralize(
          units,
          this.dataset.wordChildOne,
          this.dataset.wordChildFew,
          this.dataset.wordChildOther
        );

        if (this.liveNoteText) {
          this.liveNoteText.textContent = fillTemplate(
            this.dataset.liveNote,
            {
              units: units,
              childWord: childWord,
              qty: suggested,
              setWord: setWord,
              per: this.addonPerUnits
            }
          );
        }
        this.liveNote.hidden = false;
      }

      buildItems() {
        var self = this;
        var items = [];

        if (this.baseVariantId) {
          items.push({
            id: Number(this.baseVariantId),
            quantity: this.units()
          });
        }

        this.addons.forEach(function (addon) {
          if (!self.isActive(addon) || !addon.variantId) return;
          items.push({
            id: Number(addon.variantId),
            quantity: self.addonQty(addon)
          });
        });

        return items;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent =
          message || this.errorEl.dataset.genericError || '';
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      onSubmit(event) {
        var drawer = document.getElementById('MstCartDrawer');
        // Cart type = page: let the native single-line POST through rather
        // than silently dropping the add-ons — see the no-JS note below.
        if (!drawer) return;

        event.preventDefault();

        if (
          this.submitBtn &&
          (this.submitBtn.disabled ||
            this.submitBtn.getAttribute('aria-busy') === 'true')
        ) {
          return;
        }

        var items = this.buildItems();
        if (!items.length) return;

        var self = this;
        this.clearError();
        if (this.submitBtn) this.submitBtn.setAttribute('aria-busy', 'true');

        fetch(this.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            if (data.status && data.status !== 200) {
              throw new Error(data.description || data.message || '');
            }

            if (data.sections) {
              patchSectionHTML(
                data.sections['cart-drawer'],
                '[data-cart-drawer-inner]',
                document.querySelector('[data-cart-drawer-inner]')
              );
              patchSectionHTML(
                data.sections['cart-icon-bubble'],
                '.shopify-section',
                document.getElementById('cart-icon-bubble-wrap')
              );
            }

            return fetch(self.cartUrl, { headers: { Accept: 'application/json' } })
              .then(function (response) {
                return response.json();
              })
              .then(function (cart) {
                var detail = { cart: cart };
                document.dispatchEvent(
                  new CustomEvent('mst:cart:updated', { detail: detail })
                );
                if (window.MST && window.MST.pubsub) {
                  window.MST.pubsub.publish('mst:cart:updated', detail);
                }
              })
              .catch(function () {
                document.dispatchEvent(
                  new CustomEvent('mst:cart:updated', { detail: { cart: null } })
                );
              })
              .then(function () {
                if (typeof drawer.open === 'function') {
                  drawer.open(self.submitBtn);
                } else {
                  drawer.setAttribute('open', '');
                }
              });
          })
          .catch(function (error) {
            self.showError(error && error.message);
          })
          .finally(function () {
            if (self.submitBtn) self.submitBtn.removeAttribute('aria-busy');
          });
      }
    }
  );
})();
